import React, { useState } from "react";
import { RecallCollection } from "../types";
import {
  FaArrowLeft,
  FaEye,
  FaEyeSlash,
  FaCheck,
  FaTimes,
  FaChartLine,
  FaHistory,
} from "react-icons/fa";
import { ShareButtons } from "./ShareButtons";
import "../styles_1/FlashcardGenerator.css";

interface RecallViewerProps {
  sheet: RecallCollection;
  onBack: () => void;
  onUpdate: (updatedSheet: RecallCollection) => void;
}

export const RecallViewer: React.FC<RecallViewerProps> = ({
  sheet,
  onBack,
  onUpdate,
}) => {
  const [revealedIndices, setRevealedIndices] = useState<Set<number>>(
    new Set()
  );
  const [marks, setMarks] = useState<
    Record<number, "correct" | "incorrect" | null>
  >({});
  const [showHistory, setShowHistory] = useState(false);

  // --- HELPER TO FIX BLANK DATA (Outside component for performance) ---
  const getSafeText = (item: any, type: "q" | "a"): string => {
    if (type === "q") {
      return (
        item.cue ||
        item.question ||
        item.front ||
        item.term ||
        item.prompt ||
        "(No Question Text)"
      );
    } else {
      return (
        item.recall ||
        item.answer ||
        item.back ||
        item.definition ||
        item.response ||
        "(No Answer Text)"
      );
    }
  };

  // --- VISIBILITY CONTROLS ---
  const toggleReveal = (index: number) => {
    const newRevealed = new Set(revealedIndices);
    if (newRevealed.has(index)) newRevealed.delete(index);
    else newRevealed.add(index);
    setRevealedIndices(newRevealed);
  };

  const setAllVisibility = (visible: boolean) => {
    if (visible) {
      const allIndices = new Set(sheet.items.map((_, i) => i));
      setRevealedIndices(allIndices);
    } else {
      setRevealedIndices(new Set());
    }
  };

  const revealNextBatch = (count: number) => {
    const newRevealed = new Set(revealedIndices);
    let added = 0;
    for (let i = 0; i < sheet.items.length; i++) {
      if (!newRevealed.has(i)) {
        newRevealed.add(i);
        added++;
        if (added >= count) break;
      }
    }
    setRevealedIndices(newRevealed);
  };

  // --- MARKING LOGIC ---
  const handleMark = (index: number, status: "correct" | "incorrect") => {
    setMarks((prev) => ({ ...prev, [index]: status }));
  };

  const getScore = () => {
    const correctCount = Object.values(marks).filter(
      (m) => m === "correct"
    ).length;
    return {
      correct: correctCount,
      total: sheet.items.length,
      percentage: Math.round((correctCount / sheet.items.length) * 100) || 0,
    };
  };

  const handleSubmitSession = () => {
    const { correct, total, percentage } = getScore();
    const newAttempt = {
      timestamp: new Date().toISOString(),
      score: correct,
      total: total,
      percentage: percentage,
    };
    const updatedSheet = {
      ...sheet,
      attempts: [newAttempt, ...(sheet.attempts || [])],
    };
    onUpdate(updatedSheet);
    alert(`📝 Session Saved!\n\nScore: ${percentage}% (${correct}/${total})`);
  };

  return (
    <div
      className="generator"
      style={{ maxWidth: "1000px", margin: "0 auto", paddingBottom: "100px" }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "30px",
          borderBottom: "1px solid #e2e8f0",
          paddingBottom: "20px",
        }}
      >
        <div>
          <button
            onClick={onBack}
            className="secondary-btn"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              fontSize: "0.9rem",
            }}
          >
            <FaArrowLeft /> Back
          </button>
        </div>
        <div style={{ textAlign: "right" }}>
          <h1
            style={{
              margin: "0 0 5px 0",
              color: "#1e293b",
              fontSize: "1.8rem",
            }}
          >
            {sheet.name}
          </h1>
          <div
            style={{
              color: "#64748b",
              fontSize: "0.9rem",
              display: "flex",
              gap: "15px",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <span>{sheet.subject}</span>
            <span>•</span>
            <button
              onClick={() => setShowHistory(!showHistory)}
              style={{
                background: "none",
                border: "none",
                color: "#8b5cf6",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "5px",
                fontWeight: 600,
              }}
            >
              <FaHistory /> {showHistory ? "Hide Logs" : "View Data Logs"}
            </button>
          </div>
        </div>
      </div>

      {/* HISTORY LOGS */}
      {showHistory && (
        <div
          style={{
            background: "#f8fafc",
            padding: "20px",
            borderRadius: "12px",
            marginBottom: "30px",
            border: "1px solid #e2e8f0",
          }}
        >
          <h3 style={{ marginTop: 0, color: "#334155" }}>Attempt History</h3>
          {!sheet.attempts || sheet.attempts.length === 0 ? (
            <p style={{ color: "#94a3b8", fontStyle: "italic" }}>
              No recorded attempts yet.
            </p>
          ) : (
            <div style={{ maxHeight: "200px", overflowY: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.9rem",
                }}
              >
                <thead>
                  <tr style={{ textAlign: "left", color: "#64748b" }}>
                    <th style={{ padding: "8px" }}>Date</th>
                    <th style={{ padding: "8px" }}>Score</th>
                    <th style={{ padding: "8px" }}>Accuracy</th>
                  </tr>
                </thead>
                <tbody>
                  {sheet.attempts.map((attempt, idx) => (
                    <tr key={idx} style={{ borderTop: "1px solid #cbd5e1" }}>
                      <td style={{ padding: "8px" }}>
                        {new Date(attempt.timestamp).toLocaleString()}
                      </td>
                      <td style={{ padding: "8px" }}>
                        {attempt.score}/{attempt.total}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          fontWeight: "bold",
                          color:
                            attempt.percentage >= 80
                              ? "#16a34a"
                              : attempt.percentage >= 50
                                ? "#ca8a04"
                                : "#dc2626",
                        }}
                      >
                        {attempt.percentage}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* CONTROL BAR */}
      <div
        style={{
          background: "white",
          padding: "15px",
          borderRadius: "12px",
          marginBottom: "25px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          border: "1px solid #e2e8f0",
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={() => setAllVisibility(true)}
            className="secondary-btn"
            style={{ fontSize: "0.85rem" }}
          >
            <FaEye /> Show All
          </button>
          <button
            onClick={() => setAllVisibility(false)}
            className="secondary-btn"
            style={{ fontSize: "0.85rem" }}
          >
            <FaEyeSlash /> Hide All
          </button>
          <button
            onClick={() => revealNextBatch(3)}
            className="secondary-btn"
            style={{
              fontSize: "0.85rem",
              color: "#8b5cf6",
              borderColor: "#8b5cf6",
            }}
          >
            Reveal Next 3
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <span
            style={{ fontWeight: 700, fontSize: "1.1rem", color: "#334155" }}
          >
            Score: {getScore().correct} / {sheet.items.length}
          </span>
          <button
            onClick={handleSubmitSession}
            className="primary-btn"
            style={{
              padding: "10px 20px",
              background: "#10b981",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FaChartLine /> Submit & Save
          </button>
        </div>
      </div>

      {/* LIST OF QUESTIONS */}
      <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
        {sheet.items.map((item, index) => {
          const isRevealed = revealedIndices.has(index);
          const status = marks[index];
          let borderColor = "#e2e8f0";
          let bg = "white";
          if (status === "correct") {
            borderColor = "#22c55e";
            bg = "#f0fdf4";
          }
          if (status === "incorrect") {
            borderColor = "#ef4444";
            bg = "#fef2f2";
          }

          // SAFE DATA EXTRACTION
          const questionText = getSafeText(item, "q");
          const answerText = getSafeText(item, "a");

          return (
            <div
              key={index}
              style={{
                background: bg,
                borderRadius: "12px",
                padding: "20px",
                border: `2px solid ${borderColor}`,
                boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                transition: "all 0.2s",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "20px",
                }}
              >
                <div style={{ flex: 1 }}>
                  {/* QUESTION */}
                  <div
                    style={{
                      fontWeight: 600,
                      color: "#334155",
                      marginBottom: "12px",
                      fontSize: "1.05rem",
                    }}
                  >
                    <span
                      style={{
                        color: "#94a3b8",
                        marginRight: "10px",
                        fontSize: "0.9rem",
                      }}
                    >
                      Q{index + 1}.
                    </span>
                    {questionText}
                  </div>

                  {/* ANSWER BOX */}
                  <div
                    onClick={() => toggleReveal(index)}
                    style={{
                      background: isRevealed ? "transparent" : "#f1f5f9",
                      padding: "12px",
                      borderRadius: "8px",
                      border: "1px dashed #cbd5e1",
                      cursor: "pointer",
                      minHeight: "45px",
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {isRevealed ? (
                      <span style={{ color: "#1e293b" }}>{answerText}</span>
                    ) : (
                      <span style={{ color: "#94a3b8", fontSize: "0.9rem" }}>
                        Click to reveal answer
                      </span>
                    )}

                    <div
                      style={{
                        position: "absolute",
                        right: "10px",
                        color: "#94a3b8",
                      }}
                    >
                      {isRevealed ? <FaEyeSlash /> : <FaEye />}
                    </div>
                  </div>
                </div>

                {/* MARKING BUTTONS */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <button
                    onClick={() => handleMark(index, "correct")}
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      border: "none",
                      cursor: "pointer",
                      background: status === "correct" ? "#22c55e" : "#e2e8f0",
                      color: status === "correct" ? "white" : "#94a3b8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1rem",
                    }}
                    title="Mark Correct"
                  >
                    <FaCheck />
                  </button>
                  <button
                    onClick={() => handleMark(index, "incorrect")}
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      border: "none",
                      cursor: "pointer",
                      background:
                        status === "incorrect" ? "#ef4444" : "#e2e8f0",
                      color: status === "incorrect" ? "white" : "#94a3b8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1rem",
                    }}
                    title="Mark Incorrect"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: '60px', borderTop: '1px solid #e2e8f0', paddingTop: '30px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <p style={{ color: '#64748b', fontWeight: 600, marginBottom: '10px' }}>Enjoying your revision? Share it with fellow aspirants!</p>
        <ShareButtons title={`Checkout my UPSC ${sheet.subject} Recall Sheet on ScoreSmart!`} />
      </div>
    </div>
  );
};
